import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  catchError,
  finalize,
  forkJoin,
  Observable,
  of,
  shareReplay,
  Subject,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';
import { ApiService } from '../services/api.service';
import {
  Credentials,
  LoginResponse,
  RefreshResponse,
  ResetPasswordReqBody,
  SESSION_END_REASON_KEY,
  SessionEndReason,
  User,
} from './auth.models';
import { UserRole } from '../employees/employees.model';
import { SettingsService } from '../settings/settings.service';
import { PermissionService } from '../services/permission.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _baseUrl: string = 'auth/jwt';
  private _isLoggedInSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(this.hasToken());
  private _isInitialDataLoadingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _initializationRequest$: Observable<any> | null = null;
  private _refreshRequest$: Observable<RefreshResponse> | null = null;

  /** Aborts an in-flight refresh so it cannot write tokens back after a forced logout. */
  private _forcedLogout$: Subject<void> = new Subject<void>();

  /** Latches on forceLogout so a late response cannot revive the session. */
  private _isSessionEnded: boolean = false;

  public isInitialDataLoading$: Observable<boolean> = this._isInitialDataLoadingSubject.asObservable();
  public user$!: Observable<User | null>;

  constructor(
    private _apiService: ApiService,
    private _router: Router,
    private _settingsService: SettingsService,
    private _permissionService: PermissionService
  ) {
    this.user$ = this._settingsService.currentUser$;
  }

  login(credentials: Credentials): Observable<LoginResponse> {
    return this._apiService
      .post<LoginResponse>(`${this._baseUrl}/create/`, credentials)
      .pipe(tap((): void => this._clearSessionEnded()));
  }

  logout(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    this._settingsService.clearUser();
    this._permissionService.clear();
    this._isLoggedInSubject.next(false);
    void this._router.navigate(['/login']);
  }

  /**
   * Ends the session the server has already refused. Refreshing is pointless here: the
   * refresh token is dead too, and retrying loops the user back to login with no reason
   * given. Runs once, so a burst of superseded responses produces one redirect.
   */
  public forceLogout(reason: SessionEndReason): void {
    if (this._isSessionEnded) {
      return;
    }

    this._isSessionEnded = true;

    // Drops any refresh already in flight and any request waiting on it.
    this._forcedLogout$.next();
    this._refreshRequest$ = null;
    this._initializationRequest$ = null;

    sessionStorage.setItem(SESSION_END_REASON_KEY, reason);
    this.logout();
  }

  /** Read once by the login screen, which explains why the user landed there. */
  public consumeSessionEndReason(): SessionEndReason | null {
    const reason = sessionStorage.getItem(SESSION_END_REASON_KEY) as SessionEndReason | null;
    sessionStorage.removeItem(SESSION_END_REASON_KEY);

    return reason;
  }

  private _clearSessionEnded(): void {
    this._isSessionEnded = false;
    sessionStorage.removeItem(SESSION_END_REASON_KEY);
  }

  isLoggedIn(): Observable<boolean> {
    return this._isLoggedInSubject.asObservable();
  }

  getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  setLoggedInStatus(status: boolean): void {
    this._isLoggedInSubject.next(status);
  }

  getCurrentUser(): User | null {
    return this._settingsService.getCurrentUser();
  }

  private hasToken(): boolean {
    return !!localStorage.getItem('auth_token');
  }

  public resetPassword(reqBody: ResetPasswordReqBody): Observable<any> {
    return this._apiService.post('auth/users/set_password/', reqBody);
  }

  public resetPasswordForAdmin(userId: string, reqBody: any): Observable<any> {
    return this._apiService.patch(`auth/users/${userId}/password_reset_for_admin/`, reqBody);
  }

  /**
   * The server rotates refresh tokens and blacklists the old one on every call, so the
   * replacement has to be stored and concurrent callers have to share a single request.
   * A second call carrying an already-spent token comes back 401 and logs the user out.
   */
  public refreshToken(): Observable<RefreshResponse> {
    if (this._refreshRequest$) {
      return this._refreshRequest$;
    }

    const refresh = localStorage.getItem('refresh_token');

    if (!refresh) {
      return throwError(() => new Error('No refresh token stored'));
    }

    this._refreshRequest$ = this._apiService.post<RefreshResponse>(`${this._baseUrl}/refresh/`, { refresh }).pipe(
      tap((response: RefreshResponse) => {
        // A response that lands after forceLogout must not re-authenticate the session.
        if (this._isSessionEnded) {
          return;
        }

        localStorage.setItem('auth_token', response.access);

        if (response.refresh) {
          localStorage.setItem('refresh_token', response.refresh);
        }
      }),
      takeUntil(this._forcedLogout$),
      finalize(() => {
        this._refreshRequest$ = null;
      }),
      shareReplay(1)
    );

    return this._refreshRequest$;
  }

  public initializeSession(): Observable<any> {
    if (this._initializationRequest$) {
      return this._initializationRequest$;
    }

    this._isInitialDataLoadingSubject.next(true);

    this._initializationRequest$ = forkJoin({
      user: this._settingsService.get().pipe(catchError(() => of(null))),
      permissions: this._permissionService.fetchMyPermissions().pipe(catchError(() => of(null))),
    }).pipe(
      finalize(() => {
        this._isInitialDataLoadingSubject.next(false);
        this._initializationRequest$ = null;
      }),
      shareReplay(1)
    );

    return this._initializationRequest$;
  }
}
